import React from "react";
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Stack,
    Paper,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowBack, ArrowForward, Save } from "@mui/icons-material";
import toast from "react-hot-toast";

// Import step components
import Step1SelectProductType from "./Step1SelectProductType";
import Step2ProductDetails from "./Step2ProductDetails";
import Step3GeneralInfo from "./Step3GeneralInfo";

// ✅ Import Custom Hook
import useProductForm from "../../../hooks/products/useProductForm";

// Import API hooks
import useCreateProduct from "../../../hooks/products/useCreateProduct";
import useUpdateProduct from "../../../hooks/products/useUpdateProduct";

// ✅ Import Constants
import { getFormSteps } from "./helpers/formConstants";

/**
 * Multi-Step Product Form Wrapper
 */
const ProductFormWrapper = ({
    isEdit = false,
    currentData = null,
    onSubmitSuccess
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    // ✅ استخدام Custom Hook
    const {
        currentStep,
        formData,
        setFormData,
        errors,
        validateStep,
        handleNext,
        handleBack,
        buildPayload,
    } = useProductForm(isEdit, currentData, isArabic);

    // Create/Update product hooks
    const { createProductMutate, isPending: isCreating } = useCreateProduct(onSubmitSuccess);
    const { updateProductMutate, isPending: isUpdating } = useUpdateProduct(onSubmitSuccess);

    const isPending = isCreating || isUpdating;

    // ✅ استخدام getFormSteps
    const steps = getFormSteps(isArabic);

    // Handle form submission
    const handleSubmit = () => {
        const stepErrors = validateStep(currentStep);
        if (Object.keys(stepErrors).length > 0) {
            toast.error(
                isArabic
                    ? "الرجاء تعبئة جميع الحقول المطلوبة بشكل صحيح"
                    : "Please fill all required fields correctly"
            );
            return;
        }

        const payload = buildPayload();

        if (isEdit) {
            updateProductMutate({ id: currentData?.id, req: payload });
        } else {
            createProductMutate(payload);
        }
    };

    // Render current step content
    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return !isEdit ? (
                    <Step1SelectProductType
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                    />
                ) : null;

            case 1:
                return (
                    <Step3GeneralInfo
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        isEdit={isEdit}
                        currentData={currentData}
                    />
                );

            case 2:
                return (
                    <Step2ProductDetails
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ width: "100%", p: 3 }}>
            {/* Page Title */}
            <Typography variant="h4" sx={{ mb: 3 }}>
                {isEdit
                    ? isArabic
                        ? "تعديل المنتج"
                        : "Edit Product"
                    : isArabic
                        ? "إنشاء منتج جديد"
                        : "Create New Product"}
            </Typography>

            {/* Stepper */}
            <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
                {steps.map((step, index) => {
                    if (isEdit && index === 0) {
                        return null;
                    }

                    return (
                        <Step key={index}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            {/* Step Content */}
            <Box sx={{ mb: 3 }}>{renderStepContent()}</Box>

            {/* Navigation Buttons */}
            <Paper sx={{ p: 3 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    {/* Back Button */}
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={currentStep === (isEdit ? 1 : 0)}
                        startIcon={isArabic ? <ArrowForward /> : <ArrowBack />}
                        sx={{ gap: 1 }}
                    >
                        {isArabic ? "السابق" : "Back"}
                    </Button>

                    {/* Progress Text */}
                    <Typography variant="body2" color="text.secondary">
                        {isEdit
                            ? isArabic
                                ? `الخطوة ${currentStep} من ${steps.length - 1}`
                                : `Step ${currentStep} of ${steps.length - 1}`
                            : isArabic
                                ? `الخطوة ${currentStep + 1} من ${steps.length}`
                                : `Step ${currentStep + 1} of ${steps.length}`}
                    </Typography>

                    {/* Next/Submit Button */}
                    {currentStep < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            endIcon={isArabic ? <ArrowBack /> : <ArrowForward />}
                            sx={{ gap: 1 }}
                        >
                            {isArabic ? "التالي" : "Next"}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={isPending}
                            startIcon={<Save />}
                            sx={{ gap: 1 }}
                        >
                            {isPending
                                ? isArabic
                                    ? "جاري الحفظ..."
                                    : "Saving..."
                                : isEdit
                                    ? isArabic
                                        ? "تحديث المنتج"
                                        : "Update Product"
                                    : isArabic
                                        ? "حفظ المنتج"
                                        : "Save Product"}
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

export default ProductFormWrapper;
